import { Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import { Draggable, useDialog, useMenu } from '@prismamedia/one-components';
import { SectionFragment } from '__generated__/queries-newsletter';
import { BasicDroppable } from 'components/BasicDroppable';
import { EditableDraft } from 'components/EditableDraft';
import { EditorState } from 'draft-js';
import { BlockItemType } from 'pages/CampaignPage/CampaignEditPage/ContentPage/Items/utils';
import {
  actions,
  CampaignSectionAction,
} from 'pages/CampaignPage/CampaignEditPage/ContentPage/reducer';
import { Block } from 'pages/CampaignPage/CampaignEditPage/ContentPage/Sections/Block';
import { useDisplayArticles } from 'pages/CampaignPage/CampaignEditPage/ContentPage/utils/useDisplayArticles';
import { Dispatch, FC, memo, MouseEvent, useEffect, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { v4 as uuid } from 'uuid';
import { MAIN_TITLE_SECTION_PLACEHOLDER } from './constants';
import { ContentFromURLDialog } from './ContentFromURLDialog';
import { useStyles } from './styles';
import { ArticleFromUrl } from './types';

interface SectionProps {
  section: SectionFragment;
  dispatch: Dispatch<CampaignSectionAction>;
  disableDrag: boolean;
  compactView: boolean;
  isTemplateFromNlf: boolean;
  maxBlocksAmount?: number;
}

export const Section: FC<SectionProps> = memo(
  ({
    section,
    dispatch,
    disableDrag,
    compactView,
    isTemplateFromNlf = false,
    maxBlocksAmount,
  }) => {
    const classes = useStyles();
    const { openMenu, closeMenu } = useMenu();
    const { isMobile } = useDisplayArticles();
    const { openDialog, closeDialog } = useDialog();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [
      maxBloksAmountReached,
      setMaxBlocksAmountReached,
    ] = useState<boolean>(false);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      if (isMobile) {
        setDrawerOpen(true);
      } else {
        openMenu(event.currentTarget, [
          <MenuItem
            key="libre"
            onClick={() => {
              handleMenuClick('libre');
            }}
          >
            Contenu libre
          </MenuItem>,
          <MenuItem
            key="autoPromo"
            onClick={() => {
              handleMenuClick('autoPromo');
            }}
          >
            Auto-Promo
          </MenuItem>,
          <MenuItem
            key="copy"
            onClick={() => {
              openDialog(
                <ContentFromURLDialog
                  closeDialog={closeDialog}
                  addBlockFromArticle={addBlockFromArticle}
                />,
                { maxWidth: 'md', fullWidth: true },
              );
            }}
          >
            Récupérer le contenu d'un article
          </MenuItem>,
        ]);
      }
    };

    const handleMenuClick = (blockType: string) => {
      closeMenu();
      dispatch(actions.addBlock({ sectionId: section.id, blockType }));
    };

    const addBlockFromArticle = ({
      title,
      url,
      content,
      media,
    }: ArticleFromUrl) => {
      closeMenu();
      setDrawerOpen(false);
      !maxBloksAmountReached &&
        dispatch(
          actions.addBlock({
            sectionId: section.id,
            blockType: 'article',
            articleData: {
              id: uuid(),
              title,
              url,
              content,
              mediaForDisplay: null,
              media,
            },
          }),
        );
    };

    useEffect(() => {
      maxBlocksAmount &&
        setMaxBlocksAmountReached(section.blocks.length >= maxBlocksAmount);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section]);

    return (
      <div className={classes.root}>
        {isTemplateFromNlf ? (
          <Typography
            className={classes.sectionContentCount}
          >{`${section.blocks.length}/${maxBlocksAmount} contenu(s)`}</Typography>
        ) : (
          <IconButton
            disabled={isTemplateFromNlf}
            className={classes.deleteSection}
            onClick={() =>
              dispatch(actions.deleteSection({ sectionId: section.id }))
            }
          >
            <Delete />
          </IconButton>
        )}

        {/**
         * Main title of section
         */}
        <EditableDraft
          className={classes.title}
          content={section.title}
          onDraftChange={(value: EditorState) => {
            dispatch(
              actions.updateField({
                field: 'title',
                sectionId: section.id,
                value,
              }),
            );
          }}
          placeholder={MAIN_TITLE_SECTION_PLACEHOLDER}
          withCustomToolbar={true}
        />

        <BasicDroppable
          droppableId={`subjectSection_${section.id}`}
          droppableType="article"
          droppableKey={`subjectSection_${section.id}`}
          className={classes.dropStyle}
        >
          {section.blocks.map((block, i) => (
            <Draggable
              draggableId={block.id}
              key={block.id}
              index={i}
              isDragDisabled={disableDrag}
            >
              {(dragProvided: DraggableProvided) => (
                <div
                  ref={dragProvided.innerRef}
                  {...dragProvided.draggableProps}
                  {...dragProvided.dragHandleProps}
                >
                  <Block
                    key={block.id}
                    blockItem={(block as unknown) as BlockItemType}
                    dispatch={dispatch}
                    sectionId={section.id}
                    compactView={compactView}
                  />
                </div>
              )}
            </Draggable>
          ))}
        </BasicDroppable>
        {maxBloksAmountReached ? (
          <Typography variant="body2">Maximum de contenu atteint</Typography>
        ) : (
          <Box display="flex" alignItems="center">
            <Typography variant="body2">Glisser ici ou</Typography>
            <Button className={classes.addAction} onClick={handleClick}>
              Ajouter un nouveau contenu
            </Button>
            <Drawer
              anchor="bottom"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
            >
              <List>
                <ListItem
                  button
                  onClick={() => {
                    handleMenuClick('libre');
                  }}
                >
                  <ListItemText primary="Contenu libre" />
                </ListItem>
                <ListItem
                  button
                  onClick={() => {
                    handleMenuClick('autoPromo');
                  }}
                >
                  <ListItemText primary="Auto-Promo" />
                </ListItem>
              </List>
            </Drawer>
          </Box>
        )}
      </div>
    );
  },
);
